import React, { useState, useEffect, useRef } from 'react'
import useStore from '../hooks/useStore'
import {
  computeUnstoppableStreak,
  getMilestoneMessage,
  MILESTONE_DAYS,
  isMilestoneShown,
  markMilestoneShown,
} from '../utils/streakUtils'

const SHIP_LABELS = { yes: '✓  Yes', partial: '~  Partial', no: '✗  No' }
const SHIP_RESPONSES = {
  yes: 'Clean close. Stack another one tomorrow.',
  partial: "Progress counts. Name what's left in tomorrow's seed.",
  no: "No judgment. Name the blocker so it doesn't repeat."
}

function ScorePanel() {
  const {
    wScore,
    cScore,
    tScore,
    focusMinutesTotal,
    reflection,
    wShipped,
    loopBlocker,
    tomorrowSeed,
    history,
    loopClosed,
    updateFormData,
    saveDay,
    closeLoop,
    saveReflectionDraft,
    saveLoopDraft,
    showToast
  } = useStore()

  const [endWScore, setEndWScore] = useState(wScore)
  const [endCScore, setEndCScore] = useState(cScore)
  const [endTScore, setEndTScore] = useState(tScore)
  const [justClosedThisSession, setJustClosedThisSession] = useState(false)
  const [closingInProgress, setClosingInProgress] = useState(false)
  const reflectionTimer = useRef(null)
  const loopTimer = useRef(null)

  const scheduleLoopSave = () => {
    clearTimeout(loopTimer.current)
    loopTimer.current = setTimeout(() => saveLoopDraft(), 800)
  }

  useEffect(() => { setEndWScore(wScore) }, [wScore])
  useEffect(() => { setEndCScore(cScore) }, [cScore])
  useEffect(() => { setEndTScore(tScore) }, [tScore])
  useEffect(() => () => clearTimeout(loopTimer.current), [])

  const calculateScore = () => (endWScore * endCScore) + endTScore

  const getGrade = (score) => {
    if (score >= 100) return { grade: 'S', msg: 'Exceptional. You operated at the highest level.', bg: 'rgba(232,184,75,0.08)', bc: 'rgba(232,184,75,0.3)', gc: 'var(--gold2)' }
    if (score >= 80)  return { grade: 'A', msg: 'Excellent day. Clarity and focus multiplied powerfully.', bg: 'rgba(78,203,160,0.07)', bc: 'rgba(78,203,160,0.25)', gc: 'var(--sage2)' }
    if (score >= 60)  return { grade: 'B', msg: 'Good work. One variable can be improved tomorrow.', bg: 'rgba(77,158,232,0.07)', bc: 'rgba(77,158,232,0.25)', gc: 'var(--azure2)' }
    if (score >= 40)  return { grade: 'C', msg: 'Average. Diagnose which variable failed you today.', bg: 'rgba(232,184,75,0.05)', bc: 'rgba(232,184,75,0.15)', gc: 'var(--gold)' }
    return              { grade: 'D', msg: 'Tough day. Reset tonight. One variable at a time.', bg: 'rgba(232,104,104,0.07)', bc: 'rgba(232,104,104,0.25)', gc: 'var(--coral2)' }
  }

  const handleSaveDay = async () => {
    updateFormData({ wScore: endWScore, tScore: endTScore })
    try {
      await saveDay()
      const score = calculateScore()
      const grade = getGrade(score)
      showToast('☁', `Saved! Grade ${grade.grade} · R = ${score}`)
    } catch (error) {
      showToast('❌', 'Save failed: ' + error.message)
    }
  }

  const handleCloseToday = async () => {
    if (closingInProgress) return
    setClosingInProgress(true)
    updateFormData({ wScore: endWScore, tScore: endTScore })
    try {
      await closeLoop()
      setJustClosedThisSession(true)
      const score = calculateScore()
      const grade = getGrade(score)
      showToast('🏆', `Day closed! Grade ${grade.grade} · R = ${score}`)
    } catch (error) {
      showToast('❌', 'Close failed: ' + error.message)
    } finally {
      setClosingInProgress(false)
    }
  }

  const handleShare = (streak) => {
    const text = `Day ${streak} of being unstoppable. Shipped today's W. 🔥 #ResultOS #Unstoppable`
    try {
      navigator.clipboard.writeText(text)
      showToast('📋', 'Copied to clipboard!')
    } catch {
      showToast('📋', text)
    }
  }

  const score = calculateScore()
  const grade = getGrade(score)

  // Unstoppable streak
  const { currentStreak } = computeUnstoppableStreak(history)

  // Milestone message (shown only once per milestone per device)
  const milestoneMessage = (() => {
    if (!justClosedThisSession || wShipped !== 'yes') return null
    const match = MILESTONE_DAYS.find(d => d === currentStreak)
    if (!match) return null
    if (isMilestoneShown(match)) return null
    markMilestoneShown(match)
    return getMilestoneMessage(match)
  })()

  // Build a map of date → history row for the last-30 grid
  const historyMap = {}
  history.forEach(row => { historyMap[row.date] = row })
  const todayISO = new Date().toISOString().split('T')[0]

  // Last 30 days: oldest → newest
  const last30 = Array.from({ length: 30 }, (_, i) => {
    const d = new Date()
    d.setDate(d.getDate() - (29 - i))
    const iso = d.toISOString().split('T')[0]
    const row = historyMap[iso]
    return { iso, row, isToday: iso === todayISO }
  })

  return (
    <div className="panel active">
      <div className="pg-ey">Step 03 · Review</div>
      <div className="pg-ti">End-of-Day <em>R</em> Score</div>
      <div className="pg-su">
        Rate each variable honestly. The score reflects reality — not optimism. Use it to improve tomorrow.
      </div>
      <div className="flive">
        <span className="fR">R</span> <span className="fO">= (</span>
        <span className="fW">{endWScore}</span> <span className="fO">×</span> <span className="fC">{endCScore}</span>
        <span className="fO">) + </span><span className="fT">{endTScore}</span> <span className="fO">= </span>
        <span className="fR">{score}</span>
      </div>
      <div className="score-hero">
        <span className="score-big">{score}</span>
        <div className="score-max">out of 110</div>
        <div className="score-bd">
          <div className="sbd"><div className="sbd-v azure">{endWScore}</div><div className="sbd-l">W · Clarity</div></div>
          <div className="sbd"><div className="sbd-op">×</div></div>
          <div className="sbd"><div className="sbd-v sage">{endCScore}</div><div className="sbd-l">C · Concentration</div></div>
          <div className="sbd"><div className="sbd-op">+</div></div>
          <div className="sbd"><div className="sbd-v coral">{endTScore}</div><div className="sbd-l">T · Time</div></div>
        </div>
      </div>
      <div className="g3 mb20">
        <div className="card">
          <div className="cg azure"></div>
          <div className="c-lbl">W — Clarity</div>
          <div className="sw">
            <div className="sh score-sh-fixed">
              <span className="sq">Goal clarity</span>
              <span className="sn azure">{endWScore}</span>
            </div>
            <div className="st">
              <div className="sf2 azure" style={{width: `${endWScore * 10}%`}}></div>
              <input type="range" id="eW" min="1" max="10" value={endWScore} className="ar"
                onChange={(e) => setEndWScore(parseInt(e.target.value))} />
            </div>
            <div className="slbls"><span>Vague</span><span>Crystal clear</span></div>
          </div>
        </div>
        <div className="card score-card-auto">
          <div className="cg sage"></div>
          <div className="c-lbl">C — Concentration<span className="score-auto-badge">AUTO</span></div>
          <div className="sw">
            <div className="sh score-sh-fixed">
              <span className="sq">From focus timer</span>
              <span className="sn sage">{endCScore}</span>
            </div>
            <div className="st" style={{pointerEvents:'none'}}>
              <div className="sf2 sage" style={{width: `${endCScore * 10}%`}}></div>
              <input type="range" id="eC" min="0" max="10" value={endCScore} className="sr" readOnly onChange={() => {}} />
            </div>
            <div className="slbls"><span>{focusMinutesTotal} min logged</span><span>360 min = 10</span></div>
          </div>
        </div>
        <div className="card">
          <div className="cg coral"></div>
          <div className="c-lbl">T — Time Protected</div>
          <div className="sw">
            <div className="sh score-sh-fixed">
              <span className="sq">Time well spent</span>
              <span className="sn coral">{endTScore}</span>
            </div>
            <div className="st">
              <div className="sf2 coral" style={{width: `${endTScore * 10}%`}}></div>
              <input type="range" id="eT" min="1" max="10" value={endTScore} className="cr"
                onChange={(e) => setEndTScore(parseInt(e.target.value))} />
            </div>
            <div className="slbls"><span>Neglected</span><span>Fully balanced</span></div>
          </div>
        </div>
      </div>
      <div className="gb" style={{ background: grade.bg, borderColor: grade.bc, display: 'flex' }}>
        <div className="g-let" style={{color: grade.gc}}>{grade.grade}</div>
        <div>
          <div style={{fontSize:'10px',letterSpacing:'0.2em',color:'var(--text3)',fontFamily:'var(--ff-m)',marginBottom:'6px'}}>
            GRADE · {score}/110
          </div>
          <div className="g-msg" style={{color: grade.gc}}>{grade.msg}</div>
        </div>
      </div>

      {/* ── Close-the-Loop fields ── */}
      <div className="sec mt32">Close The Loop</div>
      <div className="card mb20">
        <div className="cg gold"></div>

        <div className="canvas-loop-section" style={{paddingTop:0}}>
          <div className="canvas-loop-field-lbl">Did the W ship?</div>
          <div className="canvas-loop-pills">
            {['yes','partial','no'].map(val => (
              <button key={val}
                className={`canvas-loop-pill ${val}${wShipped === val ? ' active' : ''}`}
                onClick={() => { updateFormData({ wShipped: val }); scheduleLoopSave() }}>
                {SHIP_LABELS[val]}
              </button>
            ))}
          </div>
          {wShipped && (
            <div className={`canvas-loop-response ${wShipped}`} style={{marginTop:'10px'}}>
              {SHIP_RESPONSES[wShipped]}
            </div>
          )}
        </div>

        <div className="canvas-loop-section canvas-loop-divider">
          <div className="canvas-loop-field-lbl">
            What blocked me?
            <span className="canvas-loop-optional">optional</span>
          </div>
          <textarea className="canvas-loop-input"
            placeholder="e.g. Slack rabbit hole, energy crash"
            value={loopBlocker}
            onChange={e => { updateFormData({ loopBlocker: e.target.value }); scheduleLoopSave() }}
            maxLength={300} rows={2} />
        </div>

        <div className="canvas-loop-section canvas-loop-divider">
          <div className="canvas-loop-field-lbl">
            Tomorrow's W seed
            <span className="canvas-loop-optional">optional</span>
          </div>
          <textarea className="canvas-loop-input"
            placeholder="Plant tomorrow's W here — it'll appear in tomorrow's Winning Result"
            value={tomorrowSeed}
            onChange={e => { updateFormData({ tomorrowSeed: e.target.value }); scheduleLoopSave() }}
            maxLength={300} rows={2} />
        </div>
      </div>

      <div className="sec mt24">Daily Reflection</div>
      <div className="card mb20">
        <div className="cg gold"></div>
        <div className="c-lbl">What happened? What to improve tomorrow?</div>
        <textarea
          rows="3"
          placeholder="e.g. W was strong, but C collapsed at 3pm due to team Slack. Tomorrow: notifications off 10am–1pm."
          value={reflection}
          onChange={(e) => {
            updateFormData({ reflection: e.target.value })
            clearTimeout(reflectionTimer.current)
            reflectionTimer.current = setTimeout(() => saveReflectionDraft(), 1500)
          }}
        />
      </div>

      {/* ── Close Today / Save buttons ── */}
      <div className="flex gap12 mb20">
        {!loopClosed ? (
          <button
            className="btn bp close-today-btn"
            onClick={handleCloseToday}
            disabled={closingInProgress}
          >
            {closingInProgress ? 'Closing…' : '✓ Close Today'}
          </button>
        ) : (
          <button className="btn bp" disabled style={{opacity:0.5,cursor:'default'}}>
            ✓ Day Closed
          </button>
        )}
        <button className="btn bg-ghost" onClick={handleSaveDay}>
          ☁ Save Draft
        </button>
      </div>

      {/* ── Post-close celebration ── */}
      {justClosedThisSession && wShipped === 'yes' && (
        <div className="loop-celebration">
          <div className="loop-celebration-streak">
            Unstoppable streak: {currentStreak} {currentStreak === 1 ? 'day' : 'days'}
          </div>
          {milestoneMessage && (
            <div className="loop-celebration-milestone">{milestoneMessage}</div>
          )}
          <button
            className="loop-share-btn"
            onClick={() => handleShare(currentStreak)}
          >
            Share
          </button>
        </div>
      )}

      {justClosedThisSession && wShipped === 'no' && (
        <div className="loop-reset-msg">
          Streak reset. Tomorrow is day 1. Show up.
        </div>
      )}

      <div className="sec mt32">Consistency Streak</div>
      <div className="g2">
        <div className="card">
          <div className="cg gold"></div>
          <div className="c-lbl">Unstoppable Streak</div>
          <div style={{fontFamily:'var(--ff-d)',fontSize:'64px',fontWeight:'900',color:'var(--gold)',lineHeight:1,marginTop:'12px'}}>
            {currentStreak}
          </div>
          <div style={{fontSize:'10px',color:'var(--text3)',fontFamily:'var(--ff-m)',marginTop:'8px'}}>
            consecutive {currentStreak === 1 ? 'day' : 'days'} shipped
          </div>
          {currentStreak === 0 && (
            <div style={{fontSize:'11px',color:'var(--text3)',fontStyle:'italic',marginTop:'10px',lineHeight:1.5}}>
              Ship today's W to start your streak.
            </div>
          )}
        </div>
        <div className="card">
          <div className="cg gold"></div>
          <div className="c-lbl">Last 30 Days</div>
          <div className="streak-grid">
            {last30.map(({iso, row, isToday}) => {
              const shipped = row?.w_shipped === 'yes'
              const closed = !!row?.closed_at
              const hasData = !!row
              const g = row?.grade || ''
              return (
                <div
                  key={iso}
                  className={`sd${shipped ? ' hit' : ''}${isToday && !closed ? ' now' : ''}`}
                  title={`${iso}${g ? ' · Grade ' + g : ''}${row?.r_score != null ? ' · R=' + row.r_score : ''}${row?.w_shipped ? ' · W ' + row.w_shipped : ''}`}
                  style={hasData && !shipped ? {borderColor:'var(--line2)',background:'rgba(255,255,255,0.04)'} : {}}
                >
                  {shipped ? g : (isToday ? '·' : '')}
                </div>
              )
            })}
          </div>
          <div style={{display:'flex',gap:'16px',marginTop:'12px',flexWrap:'wrap'}}>
            <span style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'9px',color:'var(--text3)',fontFamily:'var(--ff-m)',letterSpacing:'0.08em'}}>
              <span style={{width:'10px',height:'10px',borderRadius:'50%',background:'var(--primary)',display:'inline-block'}}></span>
              W Shipped
            </span>
            <span style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'9px',color:'var(--text3)',fontFamily:'var(--ff-m)',letterSpacing:'0.08em'}}>
              <span style={{width:'10px',height:'10px',borderRadius:'50%',border:'1px solid var(--line2)',background:'rgba(255,255,255,0.04)',display:'inline-block'}}></span>
              Partial / No
            </span>
            <span style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'9px',color:'var(--text3)',fontFamily:'var(--ff-m)',letterSpacing:'0.08em'}}>
              <span style={{width:'10px',height:'10px',borderRadius:'50%',border:'1px solid var(--line)',display:'inline-block'}}></span>
              No data
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ScorePanel
